'use client';

import { ReactNode, useMemo } from 'react';

import type { Icon } from '@personly/libs-types';
import { Record } from '@personly/libs-widgets';
import { useMediaQuery } from '@personly/libs-hooks';

import TableHeader from '@/components/table-header';

type Data = {
  axis: string;
  width?: number;
  icon?: Icon;
  header?: string;
  value?: string | ReactNode;
};

type DimensionData = {
  data: Data[];
};

type Dimension = {
  header: string;
  axis: string;
  width?: number;
  value?: string | ReactNode;
};

interface Props {
  data: DimensionData[];
  dimensions: Dimension[];
  orientation?: 'row' | 'column';
}

const key = (data: Data[], i: number) =>
  data
    .reduce<string[]>((acc, cur) => [...acc, cur.axis], [i.toString()])
    .join(':');

const Table = ({ data, dimensions, orientation: orient }: Props) => {
  const isLg = useMediaQuery('(min-width: 992px)');

  const orientation = useMemo(() => {
    if (orient) return orient;
    if (isLg) return 'row';
    return 'column';
  }, [isLg, orient]);

  return (
    <div className="text-xs">
      {orientation === 'row' && dimensions.length > 0 && data.length > 0 && (
        <Record data={dimensions} first last legend orientation={orientation}>
          {(displayHeader, displayValue, datum, legend) => (
            <>
              {displayHeader && (
                <TableHeader
                  orientation={orientation}
                  icon={datum.icon}
                  legend={legend}
                >
                  {datum.header}
                </TableHeader>
              )}
              {displayValue && <div>{datum.value}</div>}
            </>
          )}
        </Record>
      )}
      {data.map((d, i) => (
        <Record
          key={key(d.data, i)}
          data={d.data}
          first={i === 0}
          last={i === data.length - 1}
          legend={false}
          orientation={orientation}
        >
          {(displayHeader, displayValue, datum, legend) => (
            <>
              {displayHeader && (
                <TableHeader
                  orientation={orientation}
                  icon={datum.icon}
                  legend={legend}
                >
                  {datum.header}
                </TableHeader>
              )}
              {displayValue && <div>{datum.value}</div>}
            </>
          )}
        </Record>
      ))}
    </div>
  );
};

export default Table;
